import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getDocEditContent,
  saveDocContent,
  ApiError,
  type DocSavePayload,
} from "@/lib/api";
import { md } from "@/lib/markdown";

type SheetData = { name: string; rows: (string | number | null)[][] };

/**
 * In-browser document editor. Three modes by file type:
 *  - text (md/txt/json/…): plain editor, live preview for markdown
 *  - docx: rich text via contenteditable, saved back as real .docx
 *  - xlsx/csv: cell grid (values only — formulas/styling aren't preserved)
 * Saving overwrites the stored file and re-indexes it for chat.
 */
export function DocEditorModal({
  docId,
  name,
  onClose,
  onSaved,
}: {
  docId: string;
  name: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["doc-edit", docId],
    queryFn: () => getDocEditContent(docId),
    staleTime: Infinity,
    retry: false,
  });

  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Current editor state, one slot per mode.
  const [text, setText] = useState<string | null>(null);
  const [sheets, setSheets] = useState<SheetData[] | null>(null);
  const htmlRef = useRef<HTMLDivElement>(null);
  const [mdPreview, setMdPreview] = useState(false);

  useEffect(() => {
    if (!data) return;
    if (data.kind === "text") setText(data.content);
    if (data.kind === "sheets") setSheets(data.sheets);
    // html mode initializes the contenteditable directly (see render)
  }, [data]);

  const requestClose = () => {
    if (!dirty || confirm("Discard unsaved changes?")) onClose();
  };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const save = useMutation({
    mutationFn: () => {
      let payload: DocSavePayload;
      if (data?.kind === "text") payload = { kind: "text", content: text ?? "" };
      else if (data?.kind === "html")
        payload = { kind: "html", html: htmlRef.current?.innerHTML ?? "" };
      else payload = { kind: "sheets", sheets: sheets ?? [] };
      return saveDocContent(docId, payload);
    },
    onSuccess: () => {
      setDirty(false);
      setSaveError(null);
      onSaved();
      onClose();
    },
    onError: (e) => setSaveError(e instanceof ApiError ? e.message : "Save failed"),
  });

  const modal = (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-3 backdrop-blur-[2px] sm:p-6"
      onClick={requestClose}
    >
      <div
        className="chat-fade flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-token-lg border border-border bg-bg shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <header className="flex shrink-0 items-center gap-3 border-b border-border bg-bg-card px-5 py-3">
          <i className="fa-solid fa-pen-to-square text-sm text-accent" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-text-heading">
            {name}
          </span>
          {data?.kind === "sheets" && (
            <span className="text-[11px] text-text-muted">
              values only — formulas &amp; styling aren't preserved
            </span>
          )}
          {data?.kind === "html" && (
            <span className="text-[11px] text-text-muted">
              light editing — complex Word layouts may be simplified
            </span>
          )}
          {name.toLowerCase().endsWith(".md") && (
            <button
              onClick={() => setMdPreview((v) => !v)}
              className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                mdPreview ? "bg-accent-muted text-accent" : "text-text-muted hover:text-text-heading"
              }`}
            >
              <i className="fa-solid fa-eye mr-1" />
              Preview
            </button>
          )}
          {saveError && <span className="text-xs text-danger">{saveError}</span>}
          {dirty && !saveError && (
            <span className="text-xs text-text-muted">Unsaved changes</span>
          )}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button
            onClick={requestClose}
            className="grid h-8 w-8 place-items-center rounded text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        {/* body */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="grid h-full place-items-center text-text-muted">
              <i className="fa-solid fa-circle-notch fa-spin text-xl" />
            </div>
          ) : error || !data ? (
            <div className="grid h-full place-items-center px-6 text-center text-sm text-text-secondary">
              {error instanceof ApiError ? error.message : "Could not open this file for editing."}
            </div>
          ) : data.kind === "text" ? (
            <TextEditor
              value={text ?? ""}
              mdPreview={mdPreview && name.toLowerCase().endsWith(".md")}
              onChange={(v) => {
                setText(v);
                setDirty(true);
              }}
            />
          ) : data.kind === "html" ? (
            <RichEditor
              initialHtml={data.html}
              htmlRef={htmlRef}
              onDirty={() => setDirty(true)}
            />
          ) : (
            <SheetEditor
              sheets={sheets ?? []}
              onChange={(next) => {
                setSheets(next);
                setDirty(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

/* ── Text / markdown ─────────────────────────────────────────────────── */

function TextEditor({
  value,
  mdPreview,
  onChange,
}: {
  value: string;
  mdPreview: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className={`grid h-full ${mdPreview ? "grid-cols-2 divide-x divide-border" : ""}`}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="h-full w-full resize-none bg-transparent p-5 font-mono text-[13px] leading-relaxed text-text outline-none"
      />
      {mdPreview && (
        <div
          className="office-preview h-full overflow-y-auto p-5 text-sm leading-relaxed text-text"
          dangerouslySetInnerHTML={{ __html: md(value) }}
        />
      )}
    </div>
  );
}

/* ── Rich text (docx) ────────────────────────────────────────────────── */

const TOOLS: { icon: string; title: string; cmd: string; arg?: string }[] = [
  { icon: "fa-bold", title: "Bold", cmd: "bold" },
  { icon: "fa-italic", title: "Italic", cmd: "italic" },
  { icon: "fa-underline", title: "Underline", cmd: "underline" },
  { icon: "fa-heading", title: "Heading", cmd: "formatBlock", arg: "H2" },
  { icon: "fa-list-ul", title: "Bullet list", cmd: "insertUnorderedList" },
  { icon: "fa-list-ol", title: "Numbered list", cmd: "insertOrderedList" },
  { icon: "fa-rotate-left", title: "Undo", cmd: "undo" },
];

function RichEditor({
  initialHtml,
  htmlRef,
  onDirty,
}: {
  initialHtml: string;
  htmlRef: React.RefObject<HTMLDivElement>;
  onDirty: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-border-light bg-bg-card/60 px-4 py-1.5">
        {TOOLS.map((t) => (
          <button
            key={t.title}
            title={t.title}
            onMouseDown={(e) => {
              // preserve the text selection inside the contenteditable
              e.preventDefault();
              document.execCommand(t.cmd, false, t.arg);
              onDirty();
            }}
            className="grid h-7 w-7 place-items-center rounded text-xs text-text-secondary hover:bg-bg-card-hover hover:text-text-heading"
          >
            <i className={`fa-solid ${t.icon}`} />
          </button>
        ))}
      </div>
      <div
        ref={htmlRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onDirty}
        className="office-preview min-h-0 flex-1 overflow-y-auto px-8 py-6 text-sm leading-relaxed text-text outline-none"
        dangerouslySetInnerHTML={{ __html: initialHtml }}
      />
    </div>
  );
}

/* ── Sheet grid (xlsx / csv) ─────────────────────────────────────────── */

function SheetEditor({
  sheets,
  onChange,
}: {
  sheets: SheetData[];
  onChange: (next: SheetData[]) => void;
}) {
  const [active, setActive] = useState(0);
  const sheet = sheets[active];
  if (!sheet) return null;

  const cols = Math.max(1, ...sheet.rows.map((r) => r.length));

  const setCell = (r: number, c: number, v: string) => {
    const next = sheets.map((s, i) =>
      i === active
        ? {
            ...s,
            rows: s.rows.map((row, ri) => {
              if (ri !== r) return row;
              const copy = [...row];
              while (copy.length <= c) copy.push(null);
              // keep numbers numeric so Excel receives real values
              copy[c] = v === "" ? null : /^-?\d+(\.\d+)?$/.test(v.trim()) ? Number(v) : v;
              return copy;
            }),
          }
        : s,
    );
    onChange(next);
  };

  const addRow = () =>
    onChange(
      sheets.map((s, i) =>
        i === active ? { ...s, rows: [...s.rows, Array(cols).fill(null)] } : s,
      ),
    );
  const addCol = () =>
    onChange(
      sheets.map((s, i) =>
        i === active ? { ...s, rows: s.rows.map((r) => [...r, null]) } : s,
      ),
    );

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-border-light bg-bg-card/60 px-3 py-1.5">
        {sheets.map((s, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
              i === active
                ? "bg-accent-muted font-medium text-accent"
                : "text-text-muted hover:text-text-heading"
            }`}
          >
            {s.name}
          </button>
        ))}
        <span className="flex-1" />
        <button
          onClick={addRow}
          className="rounded-md px-2 py-1 text-xs text-text-muted hover:text-text-heading"
        >
          <i className="fa-solid fa-plus mr-1" />
          Row
        </button>
        <button
          onClick={addCol}
          className="rounded-md px-2 py-1 text-xs text-text-muted hover:text-text-heading"
        >
          <i className="fa-solid fa-plus mr-1" />
          Column
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <table className="border-collapse">
          <tbody>
            {sheet.rows.map((row, r) => (
              <tr key={r}>
                <td className="select-none border border-border-light bg-bg-secondary px-2 text-center text-[10px] text-text-muted">
                  {r + 1}
                </td>
                {Array.from({ length: cols }, (_, c) => (
                  <td key={c} className="border border-border-light p-0">
                    <input
                      value={row[c] == null ? "" : String(row[c])}
                      onChange={(e) => setCell(r, c, e.target.value)}
                      className="w-32 bg-transparent px-2 py-1 text-xs text-text outline-none focus:bg-accent-muted/30"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
